import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNeura } from '../../contexts/NeuraContext.tsx';
import styles from './NeuralResonanceMatrix.module.css';

interface QuantumNode {
  id: number;
  energy: number;
  entanglement: number;
  phase: number;
  position: { x: number; y: number };
  connections: number[];
  superposition: number;
  spin: number;
  coherence: number;
  aiProbability?: number;
}

interface ResonanceCell {
  frequency: number;
  amplitude: number;
  phase: number;
  coherence: number;
  quantumState: number;
}

interface EntanglementState {
  strength: number;
  type: 'quantum' | 'hybrid' | 'classical';
  stability: number;
}

// Add this utility function at the top of the file
const debounce = (fn: Function, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

export const NeuralResonanceMatrix: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { neuralActivity, aiResponse, processThought } = useNeura();
  const [resonanceLevel, setResonanceLevel] = useState(0);
  const [quantumNodes, setQuantumNodes] = useState<QuantumNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<number | null>(null);
  const [resonanceCells, setResonanceCells] = useState<ResonanceCell[]>([]);
  const [entanglementMode, setEntanglementMode] = useState(false);
  const [entanglementSource, setEntanglementSource] = useState<number | null>(null);
  const animationFrameRef = useRef<number>();
  
  // Initialize quantum neural network and resonance cells
  useEffect(() => {
    // Initialize quantum nodes with a better balanced formation
    const nodes: QuantumNode[] = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      energy: Math.random(),
      entanglement: Math.random(),
      phase: Math.random() * Math.PI * 2,
      position: {
        x: 50 + Math.cos(i * Math.PI * 2 / 8) * 32,
        y: 50 + Math.sin(i * Math.PI * 2 / 8) * 32
      },
      connections: [],
      superposition: Math.random(),
      spin: Math.random() > 0.5 ? 1 : -1,
      coherence: Math.random()
    }));
    setQuantumNodes(nodes);

    // Initialize resonance cells
    const cells: ResonanceCell[] = Array.from({ length: 16 }, () => ({
      frequency: Math.random() * 100,
      amplitude: Math.random() * 0.8 + 0.2,
      phase: Math.random() * Math.PI * 2,
      coherence: Math.random() * 0.5 + 0.5,
      quantumState: Math.random()
    }));
    setResonanceCells(cells);

    // Update cells periodically
    const interval = setInterval(() => {
      setResonanceCells(prev => prev.map(cell => ({
        ...cell,
        amplitude: Math.min(1, Math.max(0.2, cell.amplitude + (Math.random() - 0.5) * 0.1)),
        coherence: Math.min(1, Math.max(0.5, cell.coherence + (Math.random() - 0.5) * 0.1)),
        quantumState: Math.min(1, Math.max(0, cell.quantumState + (Math.random() - 0.5) * 0.1))
      })));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Update quantum nodes based on AI activity
  useEffect(() => {
    if (neuralActivity === 'processing') {
      // When AI is processing, update node properties
      setQuantumNodes(prev => prev.map(node => ({
        ...node,
        // Increase coherence and entanglement during AI processing
        coherence: Math.min(1, node.coherence + 0.2),
        entanglement: Math.min(1, node.entanglement + 0.1),
        // Make superposition more volatile during processing
        superposition: 0.3 + Math.sin(Date.now() * 0.001 + node.id) * 0.7
      })));
    }
  }, [neuralActivity]);

  // Listen for AI responses to update quantum states
  useEffect(() => {
    if (aiResponse) {
      try {
        const responseLength = aiResponse.length;
        const complexity = responseLength / 100; // Normalize to 0-1
        
        setQuantumNodes(prev => prev.map(node => ({
          ...node,
          // Update quantum properties based on AI response
          energy: 0.3 + (complexity * 0.7),
          coherence: 0.4 + (complexity * 0.6),
          aiProbability: complexity,
          // Update phase based on response characteristics
          phase: node.phase + (complexity * Math.PI * 0.5)
        })));
      } catch (error) {
        console.error('Error processing AI quantum values:', error);
      }
    }
  }, [aiResponse]);

  // Main animation loop for quantum visualization
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    let frame = 0;

    const animate = () => {
      frame++;
      // Create a gradient background
      const gradient = ctx.createRadialGradient(
        canvas.width/2, canvas.height/2, 0,
        canvas.width/2, canvas.height/2, canvas.width/2
      );
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0.99)');
      gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.97)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0.95)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add subtle background grid
      ctx.strokeStyle = 'rgba(0, 255, 255, 0.02)';
      ctx.lineWidth = 0.5;
      const gridSize = 30;
      for(let i = 0; i < canvas.width; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
      }
      for(let i = 0; i < canvas.height; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(canvas.width, i);
        ctx.stroke();
      }

      // Draw quantum entanglement connections
      quantumNodes.forEach(node => {
        node.connections.forEach(targetId => {
          const target = quantumNodes[targetId];
          const gradient = ctx.createLinearGradient(
            node.position.x * canvas.width/100,
            node.position.y * canvas.height/100,
            target.position.x * canvas.width/100,
            target.position.y * canvas.height/100
          );
          
          const phaseShift = Math.sin(frame * 0.05 + node.phase);
          gradient.addColorStop(0, `rgba(0, ${155 + phaseShift * 100}, 255, ${node.entanglement * 0.5})`);
          gradient.addColorStop(1, `rgba(0, 255, ${155 + phaseShift * 100}, ${target.entanglement * 0.5})`);
          
          ctx.beginPath();
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 4;
          
          // Quantum wave interference pattern
          const points = 20;
          for (let i = 0; i <= points; i++) {
            const t = i / points;
            const x = node.position.x + (target.position.x - node.position.x) * t;
            const y = node.position.y + (target.position.y - node.position.y) * t;
            
            const interference = Math.sin(frame * 0.05 + t * Math.PI * 4) * 
                               Math.sin(node.phase + target.phase) * 3;
            
            const wx = x + Math.cos(t * Math.PI) * interference;
            const wy = y + Math.sin(t * Math.PI) * interference;
            
            if (i === 0) ctx.moveTo(wx * canvas.width/100, wy * canvas.height/100);
            else ctx.lineTo(wx * canvas.width/100, wy * canvas.height/100);
          }
          ctx.stroke();
        });
      });

      // Draw quantum nodes
      quantumNodes.forEach((node) => {
        const x = node.position.x * canvas.width/100;
        const y = node.position.y * canvas.height/100;
        
        // Quantum superposition effect
        const superpositionRadius = 30 + Math.sin(frame * 0.05 + node.phase) * 6;
        const glowGradient = ctx.createRadialGradient(x, y, 0, x, y, superpositionRadius * 1.5);
        glowGradient.addColorStop(0, `rgba(0, 255, 255, ${node.coherence * 0.4})`);
        glowGradient.addColorStop(0.5, `rgba(0, 128, 255, ${node.coherence * 0.2})`);
        glowGradient.addColorStop(1, 'transparent');
        
        ctx.beginPath();
        ctx.fillStyle = glowGradient;
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fill();
        
        // Spin visualization
        ctx.beginPath();
        ctx.strokeStyle = `rgba(0, 255, 255, ${node.coherence})`;
        ctx.lineWidth = 4;
        for (let angle = 0; angle < Math.PI * 2; angle += 0.5) {
          const radius = 20 + Math.sin(angle * 2 + frame * 0.1 * node.spin) * 5;
          const px = x + Math.cos(angle) * radius;
          const py = y + Math.sin(angle) * radius;
          if (angle === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
        
        // Node core
        ctx.beginPath();
        ctx.fillStyle = selectedNode === node.id ? '#fff' : 
                       entanglementMode && entanglementSource === node.id ? '#00ffff' : 
                       `hsl(${180 + node.superposition * 60}, 100%, 50%)`;
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [quantumNodes, selectedNode, entanglementMode, entanglementSource]);

  // Update the resize observer effect
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    
    let animationFrameId: number;
    let previousWidth = 0;
    let previousHeight = 0;

    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (!container) return;
      
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      
      // Only resize if dimensions actually changed
      if (newWidth !== previousWidth || newHeight !== previousHeight) {
        previousWidth = newWidth;
        previousHeight = newHeight;
        
        // Set canvas size to match container
        canvas.width = newWidth;
        canvas.height = newHeight;
      }
    };

    const debouncedResize = debounce(resizeCanvas, 100);

    // Initial resize
    resizeCanvas();

    // Create resize observer with error handling
    const resizeObserver = new ResizeObserver(entries => {
      // Cancel any pending animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      // Schedule the resize on the next animation frame
      animationFrameId = requestAnimationFrame(() => {
        try {
          debouncedResize();
        } catch (error) {
          console.error('Error in resize observer:', error);
        }
      });
    });

    // Start observing with error handling
    try {
      if (canvas.parentElement) {
        resizeObserver.observe(canvas.parentElement);
      }
    } catch (error) {
      console.error('Error starting resize observer:', error);
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const handleNodeClick = (nodeIndex: number) => {
    if (entanglementMode) {
      if (entanglementSource === null) {
        setEntanglementSource(nodeIndex);
      } else if (entanglementSource !== nodeIndex) {
        setQuantumNodes(prev => {
          const newNodes = [...prev];
          // Create quantum entanglement
          if (!newNodes[entanglementSource].connections.includes(nodeIndex)) {
            newNodes[entanglementSource].connections.push(nodeIndex);
            newNodes[nodeIndex].connections.push(entanglementSource);
            // Align phases for entangled particles
            newNodes[nodeIndex].phase = newNodes[entanglementSource].phase;
            newNodes[nodeIndex].entanglement = Math.min(1, newNodes[nodeIndex].entanglement + 0.3);
            newNodes[entanglementSource].entanglement = Math.min(1, newNodes[entanglementSource].entanglement + 0.3);
          }
          return newNodes;
        });
        setEntanglementSource(null);
      }
    } else {
      setSelectedNode(nodeIndex);
      // Trigger AI processing for the selected node
      processThought(`Analyze quantum node ${nodeIndex} state and coherence patterns`);
    }
  };

  return (
    <motion.div 
      className={styles.container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <div className={styles.title}>Quantum Neural Matrix</div>
      <div className={styles.controls}>
        <motion.button
          className={`${styles.button} ${entanglementMode ? styles.active : ''}`}
          onClick={() => {
            setEntanglementMode(!entanglementMode);
            setEntanglementSource(null);
            setSelectedNode(null);
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {entanglementMode ? 'Cancel Entanglement' : 'Create Entanglement'}
        </motion.button>
      </div>
      <canvas 
        ref={canvasRef}
        className={styles.canvas}
        style={{ width: '100%', height: '100%' }}
        onClick={(e) => {
          const rect = canvasRef.current?.getBoundingClientRect();
          if (!rect) return;
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          
          const clickedNode = quantumNodes.findIndex(node => {
            const dx = node.position.x - x;
            const dy = node.position.y - y;
            return Math.sqrt(dx * dx + dy * dy) < 5;
          });
          
          if (clickedNode >= 0) {
            handleNodeClick(clickedNode);
          }
        }}
      />
      <AnimatePresence>
        {selectedNode !== null && (
          <motion.div 
            className={styles.nodeInfo}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div>Quantum Node {selectedNode}</div>
            <div>Superposition: {quantumNodes[selectedNode].superposition.toFixed(3)}</div>
            <div>Spin: {quantumNodes[selectedNode].spin > 0 ? '↑' : '↓'}</div>
            <div>Coherence: {quantumNodes[selectedNode].coherence.toFixed(3)}</div>
            <div>Entanglements: {quantumNodes[selectedNode].connections.length}</div>
            {quantumNodes[selectedNode].aiProbability && (
              <div>AI Probability: {(quantumNodes[selectedNode].aiProbability * 100).toFixed(1)}%</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}; 